// Long Tasks Timeline Visualization
// Captures Long Tasks, FCP, LCP, and TTI and draws them on a timeline
// TTI = Time to Interactive (250ms quiet window after last long task)

class LongTasksTimeline {
  constructor(container) {
    this.container = container;
    this.longTasks = [];
    this.fcp = null;
    this.lcp = null;
    this.tti = null;
    this.navigationStart = performance.timeOrigin;
    this.observers = [];
    this.ttiSettled = false;

    this.init();
  }

  init() {
    this.setupObservers();
    this.createTimeline();

    // Update timeline periodically and on load
    window.addEventListener('load', () => {
      setTimeout(() => this.updateTimeline(), 100);
    });

    // Keep updating for late LCP and TTI calculation
    let updateCount = 0;
    const updateInterval = setInterval(() => {
      this.updateTimeline();
      updateCount++;
      // Stop after TTI is settled and we've had enough updates
      if (updateCount > 20 || (this.ttiSettled && updateCount > 10)) {
        clearInterval(updateInterval);
      }
    }, 500);
  }

  setupObservers() {
    // Long Tasks Observer
    if (PerformanceObserver.supportedEntryTypes.includes('longtask')) {
      const longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.longTasks.push({
            startTime: entry.startTime,
            duration: entry.duration,
            name: entry.name
          });
        }
        // Sort by start time
        this.longTasks.sort((a, b) => a.startTime - b.startTime);
        this.calculateTTI();
        this.updateTimeline();
      });
      longTaskObserver.observe({ type: 'longtask', buffered: true });
      this.observers.push(longTaskObserver);
    }

    // FCP Observer
    if (PerformanceObserver.supportedEntryTypes.includes('paint')) {
      const paintObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            this.fcp = entry.startTime;
            this.updateTimeline();
          }
        }
      });
      paintObserver.observe({ type: 'paint', buffered: true });
      this.observers.push(paintObserver);
    }

    // LCP Observer
    if (PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint')) {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          this.lcp = entries[entries.length - 1].startTime;
          this.updateTimeline();
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(lcpObserver);
    }

    // Initial TTI calculation after a delay
    setTimeout(() => {
      this.calculateTTI();
      this.updateTimeline();
    }, 500);
  }

  calculateTTI() {
    const QUIET_WINDOW = 250; // 250ms quiet window defines TTI
    const now = performance.now();

    // TTI requires FCP - can't be interactive before first paint
    if (this.fcp === null) {
      this.tti = null;
      this.ttiSettled = false;
      return;
    }

    if (this.longTasks.length === 0) {
      // No long tasks, TTI = FCP
      this.tti = this.fcp;
      this.ttiSettled = true;
      return;
    }

    // Sort tasks by start time
    const sortedTasks = [...this.longTasks].sort((a, b) => a.startTime - b.startTime);

    // Helper: check if any task overlaps with time range [start, end]
    const hasTaskInRange = (start, end) => {
      for (const task of sortedTasks) {
        const taskEnd = task.startTime + task.duration;
        // Task overlaps if it starts before range ends AND ends after range starts
        if (task.startTime < end && taskEnd > start) {
          return true;
        }
      }
      return false;
    };

    // Check if FCP itself is valid TTI (250ms quiet window before it)
    if (!hasTaskInRange(this.fcp - QUIET_WINDOW, this.fcp)) {
      this.tti = this.fcp;
      this.ttiSettled = true;
      return;
    }

    // Find the first task end + 250ms that:
    // 1. Is >= FCP
    // 2. Has 250ms quiet window before it (no task starts in that window)
    for (let i = 0; i < sortedTasks.length; i++) {
      const taskEnd = sortedTasks[i].startTime + sortedTasks[i].duration;
      const potentialTTI = taskEnd + QUIET_WINDOW;

      // TTI must be >= FCP
      if (potentialTTI < this.fcp) {
        continue;
      }

      const nextTask = sortedTasks[i + 1];

      if (nextTask) {
        // Check if there's 250ms gap before next task starts
        if (nextTask.startTime >= potentialTTI) {
          // Found valid 250ms quiet window ending after FCP
          this.tti = potentialTTI;
          this.ttiSettled = true;
          return;
        }
        // Otherwise next task interrupts the quiet window, continue
      } else {
        // This is the last task, check if 250ms has passed since it ended
        if (now >= potentialTTI) {
          this.tti = potentialTTI;
          this.ttiSettled = true;
          return;
        } else {
          // Still waiting for 250ms quiet window to complete
          this.tti = null;
          this.ttiSettled = false;
          return;
        }
      }
    }

    // No valid TTI found yet
    this.tti = null;
    this.ttiSettled = false;
  }

  // Get only long tasks that occurred before TTI
  getTasksBeforeTTI() {
    if (this.tti === null) {
      return this.longTasks; // Show all if TTI not yet determined
    }
    return this.longTasks.filter(task => task.startTime < this.tti);
  }

  createTimeline() {
    this.container.innerHTML = `
      <div class="timeline-wrapper">
        <div class="timeline-header">
          <h2>Long Tasks Timeline (Pre-TTI)</h2>
          <div class="legend">
            <span class="legend-item"><span class="legend-color long-task"></span>Long Task (>50ms)</span>
            <span class="legend-item"><span class="legend-color fcp"></span>FCP</span>
            <span class="legend-item"><span class="legend-color lcp"></span>LCP</span>
            <span class="legend-item"><span class="legend-color tti"></span>TTI</span>
          </div>
        </div>
        <div class="stats"></div>
        <div class="timeline-container">
          <div class="timeline-track"></div>
          <div class="timeline-axis"></div>
        </div>
      </div>
    `;

    this.injectStyles();
  }

  injectStyles() {
    if (document.getElementById('long-tasks-timeline-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'long-tasks-timeline-styles';
    styles.textContent = `
      .timeline-wrapper {
        font-family: 'JetBrains Mono', 'SF Mono', 'Fira Code', monospace;
        background: linear-gradient(135deg, #0d1117 0%, #161b22 50%, #0d1117 100%);
        border: 1px solid #30363d;
        border-radius: 12px;
        padding: 24px;
        margin: 20px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05);
      }

      .timeline-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        flex-wrap: wrap;
        gap: 12px;
      }

      .timeline-header h2 {
        margin: 0;
        color: #e6edf3;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: -0.02em;
      }

      .legend {
        display: flex;
        gap: 20px;
        flex-wrap: wrap;
      }

      .legend-item {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #8b949e;
        font-size: 12px;
      }

      .legend-color {
        width: 14px;
        height: 14px;
        border-radius: 3px;
      }

      .legend-color.long-task {
        background: linear-gradient(135deg, #f85149 0%, #da3633 100%);
        box-shadow: 0 0 8px rgba(248, 81, 73, 0.4);
      }

      .legend-color.fcp {
        background: linear-gradient(135deg, #3fb950 0%, #238636 100%);
        width: 3px;
        box-shadow: 0 0 8px rgba(63, 185, 80, 0.5);
      }

      .legend-color.lcp {
        background: linear-gradient(135deg, #a371f7 0%, #8957e5 100%);
        width: 3px;
        box-shadow: 0 0 8px rgba(163, 113, 247, 0.5);
      }

      .legend-color.tti {
        background: linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%);
        width: 3px;
        box-shadow: 0 0 8px rgba(88, 166, 255, 0.5);
      }

      .stats {
        display: flex;
        gap: 24px;
        margin-bottom: 20px;
        flex-wrap: wrap;
      }

      .stat-item {
        background: rgba(48, 54, 61, 0.5);
        border: 1px solid #30363d;
        padding: 12px 16px;
        border-radius: 8px;
        min-width: 120px;
      }

      .stat-label {
        color: #8b949e;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 4px;
      }

      .stat-value {
        color: #e6edf3;
        font-size: 20px;
        font-weight: 600;
      }

      .stat-value.fcp { color: #3fb950; }
      .stat-value.lcp { color: #a371f7; }
      .stat-value.tti { color: #58a6ff; }
      .stat-value.tasks { color: #f85149; }

      .timeline-container {
        position: relative;
        height: 80px;
        margin-top: 16px;
      }

      .timeline-track {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 50px;
        background: rgba(48, 54, 61, 0.3);
        border: 1px solid #30363d;
        border-radius: 6px;
        overflow: hidden;
      }

      .timeline-axis {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 24px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding-top: 8px;
      }

      .axis-label {
        color: #6e7681;
        font-size: 10px;
        transform: translateX(-50%);
        position: absolute;
      }

      .long-task-bar {
        position: absolute;
        top: 8px;
        height: 34px;
        background: linear-gradient(180deg, #f85149 0%, #da3633 100%);
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(248, 81, 73, 0.3);
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }

      .long-task-bar:hover {
        transform: scaleY(1.1);
        box-shadow: 0 4px 16px rgba(248, 81, 73, 0.5);
        z-index: 10;
      }

      .long-task-bar span {
        color: white;
        font-size: 9px;
        font-weight: 600;
        white-space: nowrap;
        padding: 0 4px;
      }

      .marker {
        position: absolute;
        top: 0;
        height: 50px;
        width: 3px;
        border-radius: 2px;
        z-index: 5;
      }

      .marker.fcp {
        background: linear-gradient(180deg, #3fb950 0%, #238636 100%);
        box-shadow: 0 0 12px rgba(63, 185, 80, 0.6);
      }

      .marker.lcp {
        background: linear-gradient(180deg, #a371f7 0%, #8957e5 100%);
        box-shadow: 0 0 12px rgba(163, 113, 247, 0.6);
      }

      .marker.tti {
        background: linear-gradient(180deg, #58a6ff 0%, #1f6feb 100%);
        box-shadow: 0 0 12px rgba(88, 166, 255, 0.6);
      }

      .marker-label {
        position: absolute;
        top: -22px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 10px;
        font-weight: 600;
        white-space: nowrap;
        padding: 2px 6px;
        border-radius: 4px;
      }

      .marker.fcp .marker-label {
        color: #3fb950;
        background: rgba(63, 185, 80, 0.15);
      }

      .marker.lcp .marker-label {
        color: #a371f7;
        background: rgba(163, 113, 247, 0.15);
      }

      .marker.tti .marker-label {
        color: #58a6ff;
        background: rgba(88, 166, 255, 0.15);
      }

      .tooltip {
        position: fixed;
        background: #1c2128;
        border: 1px solid #30363d;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 12px;
        color: #e6edf3;
        pointer-events: none;
        z-index: 1000;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
        max-width: 280px;
      }

      .tooltip-row {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 4px;
      }

      .tooltip-row:last-child {
        margin-bottom: 0;
      }

      .tooltip-label {
        color: #8b949e;
      }

      .tooltip-value {
        font-weight: 600;
        color: #f85149;
      }
    `;
    document.head.appendChild(styles);
  }

  updateTimeline() {
    const track = this.container.querySelector('.timeline-track');
    const axis = this.container.querySelector('.timeline-axis');
    const stats = this.container.querySelector('.stats');

    if (!track) return;

    // Get only tasks before TTI
    const tasksBeforeTTI = this.getTasksBeforeTTI();

    // Calculate timeline range
    let maxTime = 5000; // Minimum 5 seconds

    if (tasksBeforeTTI.length > 0) {
      const lastTask = tasksBeforeTTI[tasksBeforeTTI.length - 1];
      maxTime = Math.max(maxTime, lastTask.startTime + lastTask.duration + 500);
    }
    if (this.fcp) maxTime = Math.max(maxTime, this.fcp + 500);
    if (this.lcp) maxTime = Math.max(maxTime, this.lcp + 500);
    if (this.tti) maxTime = Math.max(maxTime, this.tti + 500);

    // Round up to nice number
    maxTime = Math.ceil(maxTime / 1000) * 1000;

    // Update stats - only count tasks before TTI
    const totalBlockingTime = tasksBeforeTTI.reduce((sum, task) => sum + Math.max(0, task.duration - 50), 0);

    stats.innerHTML = `
      <div class="stat-item">
        <div class="stat-label">FCP</div>
        <div class="stat-value fcp">${this.fcp ? Math.round(this.fcp) + 'ms' : '—'}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">LCP</div>
        <div class="stat-value lcp">${this.lcp ? Math.round(this.lcp) + 'ms' : '—'}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">TTI</div>
        <div class="stat-value tti">${this.tti !== null ? Math.round(this.tti) + 'ms' : '—'}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">Long Tasks (Pre-TTI)</div>
        <div class="stat-value tasks">${tasksBeforeTTI.length}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">Total Blocking Time</div>
        <div class="stat-value tasks">${Math.round(totalBlockingTime)}ms</div>
      </div>
    `;

    // Clear and rebuild track
    track.innerHTML = '';

    // Draw long tasks (only those before TTI)
    tasksBeforeTTI.forEach((task, index) => {
      const left = (task.startTime / maxTime) * 100;
      const width = Math.max((task.duration / maxTime) * 100, 0.5);

      const bar = document.createElement('div');
      bar.className = 'long-task-bar';
      bar.style.left = `${left}%`;
      bar.style.width = `${width}%`;

      if (width > 3) {
        bar.innerHTML = `<span>${Math.round(task.duration)}ms</span>`;
      }

      // Tooltip
      bar.addEventListener('mouseenter', (e) => this.showTooltip(e, task));
      bar.addEventListener('mouseleave', () => this.hideTooltip());
      bar.addEventListener('mousemove', (e) => this.moveTooltip(e));

      track.appendChild(bar);
    });

    // Draw FCP marker
    if (this.fcp) {
      const fcpMarker = document.createElement('div');
      fcpMarker.className = 'marker fcp';
      fcpMarker.style.left = `${(this.fcp / maxTime) * 100}%`;
      fcpMarker.innerHTML = `<span class="marker-label">FCP ${Math.round(this.fcp)}ms</span>`;
      track.appendChild(fcpMarker);
    }

    // Draw LCP marker
    if (this.lcp) {
      const lcpMarker = document.createElement('div');
      lcpMarker.className = 'marker lcp';
      lcpMarker.style.left = `${(this.lcp / maxTime) * 100}%`;
      lcpMarker.innerHTML = `<span class="marker-label">LCP ${Math.round(this.lcp)}ms</span>`;
      track.appendChild(lcpMarker);
    }

    // Draw TTI marker
    if (this.tti !== null) {
      const ttiMarker = document.createElement('div');
      ttiMarker.className = 'marker tti';
      ttiMarker.style.left = `${(this.tti / maxTime) * 100}%`;
      ttiMarker.innerHTML = `<span class="marker-label">TTI ${Math.round(this.tti)}ms</span>`;
      track.appendChild(ttiMarker);
    }

    // Draw axis
    axis.innerHTML = '';
    const tickCount = 5;
    for (let i = 0; i <= tickCount; i++) {
      const time = (maxTime / tickCount) * i;
      const label = document.createElement('span');
      label.className = 'axis-label';
      label.style.left = `${(i / tickCount) * 100}%`;
      label.textContent = time >= 1000 ? `${(time / 1000).toFixed(1)}s` : `${time}ms`;
      axis.appendChild(label);
    }
  }

  showTooltip(e, task) {
    this.hideTooltip();

    const tooltip = document.createElement('div');
    tooltip.className = 'tooltip';
    tooltip.id = 'long-task-tooltip';
    tooltip.innerHTML = `
      <div class="tooltip-row">
        <span class="tooltip-label">Start Time:</span>
        <span class="tooltip-value">${Math.round(task.startTime)}ms</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">Duration:</span>
        <span class="tooltip-value">${Math.round(task.duration)}ms</span>
      </div>
      <div class="tooltip-row">
        <span class="tooltip-label">Blocking Time:</span>
        <span class="tooltip-value">${Math.round(Math.max(0, task.duration - 50))}ms</span>
      </div>
    `;
    document.body.appendChild(tooltip);
    this.moveTooltip(e);
  }

  moveTooltip(e) {
    const tooltip = document.getElementById('long-task-tooltip');
    if (tooltip) {
      tooltip.style.left = `${e.clientX + 12}px`;
      tooltip.style.top = `${e.clientY + 12}px`;
    }
  }

  hideTooltip() {
    const tooltip = document.getElementById('long-task-tooltip');
    if (tooltip) tooltip.remove();
  }

  destroy() {
    this.observers.forEach(obs => obs.disconnect());
    this.hideTooltip();
  }
}

// Auto-initialize if container exists
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('long-tasks-timeline');
  if (container) {
    window.longTasksTimeline = new LongTasksTimeline(container);
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LongTasksTimeline;
}


const el = document.createElement("div");
document.body.appendChild(el);
const timeline = new LongTasksTimeline(el);
